import {
  collection,
  addDoc,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  where,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { UserFeedback, FeedbackType, FeedbackStatus, FeedbackPriority, FeedbackCategory } from '../types';
import { APP_VERSION } from '../config/version';

const COLLECTION = 'feedback';

// Page-to-section mapping for auto-detection
const PAGE_SECTION_MAP: Record<string, string> = {
  'dashboard': 'Dashboard',
  'quick-select': 'Dashboard',
  'leads': 'LeadHub',
  'companies': 'LeadHub',
  'opportunities': 'LeadHub',
  'customers': 'LeadHub',
  'products': 'LeadHub',
  'documents': 'LeadHub',
  'closing-assistant': 'LeadHub',
  'insights': 'LeadHub',
  'brandbook': 'LeadBoost',
  'google-ads-overview': 'LeadBoost',
  'google-ads-campaigns': 'LeadBoost',
  'google-ads-optimization': 'LeadBoost',
  'customer-intelligence': 'LeadInsights',
  'ai-insights': 'LeadInsights',
  'engagements': 'Actions',
  'team-skills': 'Actions',
  'tasks': 'Actions',
  'stream': 'Actions',
  'emails': 'Actions',
  'time-tracking': 'Actions',
  'expenses': 'Actions',
  'business-plan': 'Actions',
  'settings': 'Settings',
  'material': 'More',
  'automate': 'More',
  'company-profile': 'More',
};

// Page display names
const PAGE_DISPLAY_NAMES: Record<string, string> = {
  'dashboard': 'Dashboard',
  'quick-select': 'Schnellauswahl',
  'leads': 'Leads',
  'companies': 'Unternehmen',
  'opportunities': 'Opportunities',
  'customers': 'Kunden',
  'products': 'Produkte',
  'documents': 'Dokumente',
  'closing-assistant': 'Closing Assistant',
  'insights': 'Insights',
  'brandbook': 'Brand Book',
  'google-ads-overview': 'Google Ads',
  'google-ads-campaigns': 'Kampagnen',
  'google-ads-optimization': 'Optimierung',
  'customer-intelligence': 'Customer Intelligence',
  'ai-insights': 'AI Insights',
  'engagements': 'Aufträge',
  'team-skills': 'Team',
  'tasks': 'Aufgaben',
  'stream': 'Stream',
  'emails': 'E-Mails',
  'time-tracking': 'Zeiterfassung',
  'expenses': 'Reisekosten',
  'business-plan': 'Business Plan',
  'settings': 'Einstellungen',
  'material': 'Material',
  'automate': 'Automatisierung',
  'company-profile': 'Firmenprofil',
};

export const feedbackService = {
  /**
   * Get app section from page ID
   */
  getAppSection(pageId: string): string {
    return PAGE_SECTION_MAP[pageId] || 'Sonstiges';
  },

  /**
   * Get display name for a page
   */
  getPageDisplayName(pageId: string): string {
    return PAGE_DISPLAY_NAMES[pageId] || pageId;
  },

  /**
   * Submit new feedback
   */
  async submitFeedback(data: {
    orgId: string;
    userId: string;
    userName: string;
    userEmail: string;
    userAvatar?: string;
    type: FeedbackType;
    category: FeedbackCategory;
    title: string;
    description: string;
    priority: FeedbackPriority;
    currentPage: string;
    screenshotUrl?: string;
  }): Promise<string> {
    // Remove undefined values - Firestore does not accept them
    const cleanData: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        cleanData[key] = value;
      }
    }

    const docRef = await addDoc(collection(db, COLLECTION), {
      ...cleanData,
      appSection: this.getAppSection(data.currentPage),
      status: 'open' as FeedbackStatus,
      browserInfo: navigator.userAgent,
      appVersion: APP_VERSION,
      upvotes: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  /**
   * Upload screenshot to Firebase Storage
   */
  async uploadScreenshot(file: File, orgId: string): Promise<string> {
    const timestamp = Date.now();
    const storageRef = ref(storage, `feedback/${orgId}/${timestamp}_${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  },

  /**
   * Subscribe to all feedback for an organization (realtime)
   */
  subscribeFeedback(
    orgId: string,
    callback: (feedbacks: UserFeedback[]) => void,
    onError?: (error: any) => void
  ): () => void {
    if (!orgId) {
      onError?.(new Error('No orgId provided'));
      return () => {};
    }

    const q = query(
      collection(db, COLLECTION),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const feedbacks: UserFeedback[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        const assignees = Array.isArray(data.assignees)
          ? data.assignees.map((a: any) => ({
              ...a,
              assignedAt: a.assignedAt instanceof Timestamp ? a.assignedAt.toDate() : a.assignedAt ? new Date(a.assignedAt) : new Date(),
            }))
          : undefined;
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
          testedAt: data.testedAt instanceof Timestamp ? data.testedAt.toDate() : data.testedAt ? new Date(data.testedAt) : undefined,
          assignees,
        } as UserFeedback;
      });
      callback(feedbacks);
    }, (error) => {
      console.error('Feedback subscription error:', error);
      onError?.(error);
    });
  },

  /**
   * Get all feedback for an organization
   */
  async getFeedbackForOrg(orgId: string): Promise<UserFeedback[]> {
    const q = query(
      collection(db, COLLECTION),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      const assignees = Array.isArray(data.assignees)
        ? data.assignees.map((a: any) => ({
            ...a,
            assignedAt: a.assignedAt instanceof Timestamp ? a.assignedAt.toDate() : a.assignedAt ? new Date(a.assignedAt) : new Date(),
          }))
        : undefined;
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
        testedAt: data.testedAt instanceof Timestamp ? data.testedAt.toDate() : data.testedAt ? new Date(data.testedAt) : undefined,
        assignees,
      } as UserFeedback;
    });
  },

  /**
   * Update feedback status (admin only)
   */
  async updateFeedbackStatus(
    feedbackId: string,
    status: FeedbackStatus,
    adminNotes?: string
  ): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    const updateData: any = {
      status,
      updatedAt: serverTimestamp(),
    };
    if (adminNotes !== undefined) {
      updateData.adminNotes = adminNotes;
    }
    if (status === 'resolved') {
      updateData.resolvedAt = serverTimestamp();
    } else if (status === 'open' || status === 'in_progress') {
      updateData.resolvedAt = null;
    }
    if (status === 'open' || status === 'in_progress') {
      updateData.testedVerified = false;
      updateData.testedBy = null;
      updateData.testedAt = null;
    }
    await updateDoc(docRef, updateData);
  },

  /**
   * Toggle upvote on feedback
   */
  async toggleUpvote(feedbackId: string, userId: string): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) return;
    
    const data = docSnap.data();
    const upvotes: string[] = data.upvotes || [];

    if (upvotes.includes(userId)) {
      await updateDoc(docRef, {
        upvotes: arrayRemove(userId),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(docRef, {
        upvotes: arrayUnion(userId),
        updatedAt: serverTimestamp(),
      });
    }
  },

  /**
   * Delete feedback (admin only)
   */
  async deleteFeedback(feedbackId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, feedbackId));
  },

  async assignUser(feedbackId: string, userId: string, userName: string): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;

    const data = docSnap.data();
    const assignees: any[] = data.assignees || [];

    if (assignees.some((a: any) => a.userId === userId)) return;

    assignees.push({
      userId,
      userName,
      assignedAt: Timestamp.now(),
    });

    await updateDoc(docRef, {
      assignees,
      updatedAt: serverTimestamp(),
    });
  },

  async unassignUser(feedbackId: string, userId: string): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return;

    const data = docSnap.data();
    const assignees: any[] = data.assignees || [];
    const updatedAssignees = assignees.filter((a: any) => a.userId !== userId);
    await updateDoc(docRef, {
      assignees: updatedAssignees,
      updatedAt: serverTimestamp(),
    });
  },

  async updateWorkNotes(feedbackId: string, workNotes: string): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    await updateDoc(docRef, {
      workNotes,
      updatedAt: serverTimestamp(),
    });
  },

  async markAsTested(feedbackId: string, testedByUserId: string, testedByName: string): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    await updateDoc(docRef, {
      testedVerified: true,
      testedBy: testedByName,
      testedAt: serverTimestamp(),
      status: 'resolved' as FeedbackStatus,
      resolvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  async unmarkAsTested(feedbackId: string): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    await updateDoc(docRef, {
      testedVerified: false,
      testedBy: null,
      testedAt: null,
      updatedAt: serverTimestamp(),
    });
  },

  async updateCategory(feedbackId: string, category: FeedbackCategory): Promise<void> {
    const docRef = doc(db, COLLECTION, feedbackId);
    await updateDoc(docRef, {
      category,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Get feedback counts by type
   */
  getCountsByType(feedbacks: UserFeedback[]): Record<FeedbackType | 'all', number> {
    return {
      all: feedbacks.length,
      bug: feedbacks.filter(f => f.type === 'bug').length,
      change_request: feedbacks.filter(f => f.type === 'change_request').length,
      feature_request: feedbacks.filter(f => f.type === 'feature_request').length,
    };
  },

  /**
   * Get feedback counts by status
   */
  getCountsByStatus(feedbacks: UserFeedback[]): Record<FeedbackStatus | 'all', number> {
    return {
      all: feedbacks.length,
      open: feedbacks.filter(f => f.status === 'open').length,
      in_progress: feedbacks.filter(f => f.status === 'in_progress').length,
      resolved: feedbacks.filter(f => f.status === 'resolved').length,
      closed: feedbacks.filter(f => f.status === 'closed').length,
    };
  },

  /**
   * Get open feedback count
   */
  getOpenCount(feedbacks: UserFeedback[]): number {
    return feedbacks.filter(f => f.status === 'open' || f.status === 'in_progress').length;
  },

  /**
   * Auto-close resolved tickets older than 14 days.
   * Uses resolvedAt as primary timestamp, falls back to updatedAt/testedAt
   * for tickets that were resolved before the resolvedAt field existed.
   */
  async autoCloseResolvedTickets(orgId: string): Promise<number> {
    const AUTO_CLOSE_DAYS = 14;
    const cutoffDate = new Date(Date.now() - AUTO_CLOSE_DAYS * 24 * 60 * 60 * 1000);
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

    const q = query(
      collection(db, COLLECTION),
      where('orgId', '==', orgId),
      where('status', '==', 'resolved')
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return 0;

    const batch = writeBatch(db);
    let count = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();

      const resolvedTs: Timestamp | null =
        data.resolvedAt ?? data.testedAt ?? data.updatedAt ?? null;

      if (!resolvedTs || resolvedTs.toMillis() > cutoffTimestamp.toMillis()) continue;

      batch.update(docSnap.ref, {
        status: 'closed' as FeedbackStatus,
        updatedAt: serverTimestamp(),
      });
      count++;
    }

    if (count > 0) {
      await batch.commit();
    }

    return count;
  },
};
